import React from 'react';
import type { LandingCommunicationContent } from '../content/types';
import type { LandingBlockTone } from './LandingPitchBlock';

export type LandingInfoBlockProps = {
  content: LandingCommunicationContent['info'];
  tone?: LandingBlockTone;
  className?: string;
};

const headingColor: Record<LandingBlockTone, string> = {
  dark: 'text-white',
  light: 'text-slate-950',
  accent: 'text-white',
};

const bodyColor: Record<LandingBlockTone, string> = {
  dark: 'text-white/75',
  light: 'text-slate-700',
  accent: 'text-white/90',
};

/**
 * LandingInfoBlock — practical "how this works" block.
 * Splits body on blank lines so seeded multi-paragraph copy renders cleanly.
 */
export default function LandingInfoBlock({
  content,
  tone = 'dark',
  className = '',
}: LandingInfoBlockProps) {
  if (!content || !content.body) return null;
  const paragraphs = content.body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div
      data-edit-tag={content.editTag}
      className={`mx-auto max-w-3xl ${className}`.trim()}
    >
      {content.headline ? (
        <h3
          className={`text-2xl font-semibold tracking-tight md:text-3xl ${headingColor[tone]}`}
        >
          {content.headline}
        </h3>
      ) : null}
      <div
        className={`${content.headline ? 'mt-4' : ''} space-y-4 text-base leading-7 md:text-lg md:leading-8 ${bodyColor[tone]}`}
      >
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </div>
  );
}